import React, { useState, useMemo, useCallback, useEffect } from "react";
import SavedInAnalyser from "./Child/SavedInAnalyser";
import RecentlyAnalysed from "./Child/RecentlyAnalysed";
import REITab from "../../../components/ui/tab";
import { useHttpGet } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY, USER_DETAIL } from "../../../utils/constants";
import { REIProgressSpinner } from "../../../components/ui";
import { ConfirmPopup } from "primereact/confirmpopup";
import { fetchFormattedAddress, shouldEnrichAddress } from "../../../utils/commonUtil";

const useAnalyserData = (refreshData: boolean) => {
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");

  const {
    data: analyserData,
    error,
    statusCode,
  } = useHttpGet(
    `${ApiEndPoints.analyser}/IndexAnalyser?MemberId=${localStorage.getItem(MEMBER_KEY)}&MemberCountryCode=${
      memberDetail.Country
    }`,
    undefined,
    refreshData
  );

  const loading = !analyserData && !error && statusCode === null;

  return { analyserData, error, loading };
};

interface ISavedAnalyserProps {
  hideExtraColumns?: boolean;
  externalRefreshData?: boolean;
  forecastForYear?: number;
}

const SavedAnalyser: React.FC<ISavedAnalyserProps> = ({
  hideExtraColumns,
  externalRefreshData,
  forecastForYear = 1,
}) => {
  const [refreshData, setRefreshData] = useState(false);
  const { analyserData, loading } = useAnalyserData(refreshData);
  const [activeTab, setActiveTab] = useState(0);
  const [savedProperties, setSavedProperties] = useState<any[]>([]);
  const [recentlyAnalysed, setRecentlyAnalysed] = useState<any[]>([]);
  const { data: watchlistData, error } = useHttpGet(
    `${ApiEndPoints.analyser}/Watchlist/${localStorage.getItem(MEMBER_KEY)}`,
    undefined,
    true
  );

  useEffect(() => {
    if (externalRefreshData !== undefined) {
      setRefreshData((prev) => !prev);
    }
  }, [externalRefreshData]);

  const reloadData = useCallback(() => {
    setRefreshData((prev) => !prev);
  }, []);

  const formatPropertyAddress = (rowData: any) => `${rowData.StreetAddress}, ${rowData.Suburb}, ${rowData.StateRegion}`;
  const formatDisplayAddress = useCallback(async (baseAddress: string): Promise<string> => {
    const trimmed = (baseAddress || "").trim();
    if (!trimmed) return "";

    const apiKey = process.env.GOOGLE_MAPS_API_KEY;
    if (!apiKey || !shouldEnrichAddress(trimmed)) return trimmed;

    const formatted = await fetchFormattedAddress(trimmed, apiKey);
    return formatted || trimmed;
  }, []);

  const mergeWatchlistAddress = useCallback(
    async (properties: any[] = []) =>
      Promise.all(
        properties.map(async (rowData: any) => {
          const watchlistItem = (watchlistData || []).find(
            (item: any) => item.PropetyAnalyserId === rowData.PropertyAnalyserId
          );
          const isInWatchlist = !!watchlistItem;
          const baseAddress = isInWatchlist
            ? formatPropertyAddress(watchlistItem)
            : rowData.PropertyAddress || rowData.PropertyName || "";
          const formattedAddress = await formatDisplayAddress(baseAddress);

          return {
            ...rowData,
            PropertyAddress: formattedAddress,
          };
        })
      ),
    [formatDisplayAddress, watchlistData]
  );

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      const saved = await mergeWatchlistAddress(analyserData?.SavedProperties || []);
      const recent = await mergeWatchlistAddress(analyserData?.RecentlyAnalysed || []);

      if (isActive) {
        setSavedProperties(saved);
        setRecentlyAnalysed(recent);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [analyserData?.RecentlyAnalysed, analyserData?.SavedProperties, mergeWatchlistAddress]);

  const tabs = useMemo(
    () => [
      {
        header: "Saved in analyser",
        content:
          savedProperties.length > 0 ? (
            <SavedInAnalyser
              data={savedProperties}
              forecastForYears={forecastForYear - 1}
              callRefreshData={reloadData}
              hideExtraColumns={hideExtraColumns}
            />
          ) : (
            <div>No saved properties found.</div>
          ),
      },
      {
        header: "Recently analysed (unsaved)",
        content:
          recentlyAnalysed.length > 0 ? (
            <RecentlyAnalysed
              data={recentlyAnalysed}
              forecastForYears={forecastForYear - 1}
              callRefreshData={reloadData}
              hideExtraColumns={hideExtraColumns}
            />
          ) : (
            <div>No recently analysed properties found.</div>
          ),
      },
    ],
    [savedProperties, recentlyAnalysed, forecastForYear, reloadData]
  );

  if (loading) {
    return (
      <div className="text-center">
        <REIProgressSpinner />
      </div>
    );
  }

  if (!analyserData) {
    return <div className="text-center">No data selected for analyser.</div>;
  }

  return (
    <>
      <ConfirmPopup />
      {/* {!hideExtraColumns && (
        <div className="w-[93%] z-10 absolute mt-0">
          <div className="flex justify-end items-center space-x-2">
            <label className=" text-sm font-medium w-[5.5rem]">Forecast for:</label>
            <Slider
              value={forecastForYear}
              onChange={(e) => setForecastForYear(Array.isArray(e.value) ? e.value[0] : e.value)}
              className="w-[150px]"
              max={10}
              min={1}
              step={1}
            />
            <label className="text-sm font-medium w-[2.75rem] ml-3">Yr {forecastForYear}</label>
          </div>
        </div>
      )} */}

      <div className="w-full bg-white">
        <REITab id="sana-tab-analyser" tabs={tabs} activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} />
      </div>
    </>
  );
};

export default SavedAnalyser;
